import VirtualBackgroundExtension, { IVirtualBackgroundProcessor } from 'agora-extension-virtual-background';
import AgoraRTC, { type ICameraVideoTrack } from 'agora-rtc-react';

import { StudioLogger } from '@studio/logger/StudioLogger';
import { AGORA_ENCODER_CONFIG } from '@studio/constants/VideoQualitySettings';

export const DEGRADED_FPS_COST_THRESHOLD = 100;
export const DEGRADED_FPS_DURATION_THRESHOLD_MS = 60000;

/* eslint-disable no-unused-vars */
export enum BACKGROUND_OPTIONS {
  BLUR = 'Blur',
  CUSTOM = 'Custom',
  NONE = 'None'
}

const LOG = new StudioLogger('agoraVirtualBackgroundLogger');

const extension = new VirtualBackgroundExtension();

let processor: IVirtualBackgroundProcessor | null = null;
let isProcessorEnabled = false;
let pipedTrack: ICameraVideoTrack | null = null;

let costData: any[] = [];
let degradedFrameRate: boolean = false;
let degradedCostStartTime: number | null = null;

/**
 * Registers an external listener for virtual background frame cost events.
 * The callback will be invoked each time the processor emits a cost value.
 * @param callback - Function invoked with the cost value on each frame cost event
 */
export const registerCostListener = (callback: (cost: any) => void): void => {
  if (!processor) {
    LOG.warn('Processor not initialized. Cost listener will not be registered.');
    return;
  }
  (processor as any).eventBus.on('cost', callback);
  LOG.info('External cost listener registered');
};

/**
 * Unregisters a previously registered external listener for frame cost events.
 * @param callback - The same callback reference that was passed to registerCostListener
 */
export const unregisterCostListener = (callback: (cost: any) => void): void => {
  if (processor) {
    (processor as any).eventBus.off('cost', callback);
    LOG.info('External cost listener unregistered');
  }
};

/**
 * Evaluates cost data and sets degradedFrameRate to true if cost has remained
 * above the threshold for over a minute. Invokes the appropriate callback when
 * degradation starts or stops.
 * @param cost - The current frame processing cost value
 * @param onDegraded - Called when cost stays above the threshold for the configured duration
 * @param onDegradedStop - Called when cost drops back below the threshold
 */
export const checkDegradedFrameRate = (cost: any, onDegraded: () => void, onDegradedStop: () => void) => {
  if (cost >= DEGRADED_FPS_COST_THRESHOLD) {
    if (!degradedCostStartTime) {
      degradedCostStartTime = new Date().getTime();
    }
    if (degradedCostStartTime && Date.now() - degradedCostStartTime >= DEGRADED_FPS_DURATION_THRESHOLD_MS) {
      // Set degradedFrameRate to true if cost has been above threshold for over a minute
      LOG.warn(`Virtual background frame cost has been degraded for over a minute`, cost);
      onDegraded();
      degradedCostStartTime = null;
      degradedFrameRate = true;
    }
  } else if (cost < DEGRADED_FPS_COST_THRESHOLD) {
    // Reset the timer and degraded frame rate flag if cost drops below threshold
    degradedFrameRate && onDegradedStop();
    degradedFrameRate = false;
    degradedCostStartTime = null;
  }
};

const logFrameCost = (cost: any) => {
  costData.push({ frameCost: Math.trunc(cost), processorEnabled: isProcessorEnabled });
  // Chunks of 5 was chosen arbitrarily to balance limiting the amount of logging
  // the cost event triggers every ~3.5s which means we'll get a log every ~17.5s when the processor is enabled
  if (costData.length >= 5) {
    const costScore = getCostScore(costData);
    const payload = { costData, costScore };
    LOG.info(`User Background Frame Cost (avg: ${costScore.avg}ms)`, payload);
    costData = []; // Clear the array after logging
  }
};

const getCostScore = (
  arr: { frameCost: number; processorEnabled: boolean }[]
): { avg: number; min: number; max: number } => {
  const frameCosts = arr.map(value => value.frameCost);
  const avg = frameCosts.reduce((sum, value) => sum + value, 0) / frameCosts.length;
  const min = Math.min(...frameCosts);
  const max = Math.max(...frameCosts);
  return { avg, min, max };
};

/**
 * Sets the processor enabled state
 * @param enabled - The new enabled state
 */
export const setProcessorEnabled = (enabled: boolean): void => {
  isProcessorEnabled = enabled;
};

/**
 * Fully tears down the processor pipeline and releases the processor.
 * The processor will be re-created on the next transformVideo call.
 * Call this when switching away from a virtual background or before recreating a track.
 * @param videoTrack - The video track to unpipe from (optional, may be null/dead)
 */
export const teardownPipeline = (videoTrack?: ICameraVideoTrack | null): void => {
  try {
    // Unpipe processor FIRST while the chain is still connected.
    // This ensures processorDestination properly clears its upstream reference.
    // If we unpipe videoTrack first, the chain breaks and processor.unpipe()
    // can leave processorDestination in a stale "already piped" state.
    if (processor) {
      processor.unpipe();
      LOG.info('Processor unpipe completed during teardown');
    }
    if (videoTrack) {
      videoTrack.unpipe();
    }
    if (processor) {
      processor.release();
      LOG.info('Processor released during pipeline teardown');
    }
  } catch (e) {
    LOG.error('Error during pipeline teardown:', e);
  }
  processor = null;
  pipedTrack = null;
  setProcessorEnabled(false);
  LOG.info('Pipeline torn down and processor released');
};

/**
 * Configuration for blur virtual background effect
 */
interface BlurVFXConfig {
  type: 'blur';
  blurDegree: number;
}

/**
 * Configuration for custom image virtual background effect
 */
interface ImageVFXConfig {
  type: 'img';
  source: HTMLImageElement;
}

/**
 * Configuration for color virtual background effect
 */
interface ColorVFXConfig {
  type: 'color';
  color: string;
}

/**
 * Type for the transformed video input
 */
interface TransformedVideoInput {
  transformedVideoTrack: ICameraVideoTrack;
  device: string;
}

/**
 * Type for all possible VFX configurations
 */
type VFXConfig = BlurVFXConfig | ImageVFXConfig | ColorVFXConfig;

type OperatingSystem = 'Windows' | 'MacOS' | 'Linux' | 'Android' | 'iOS' | 'Unknown';

/**
 * Helper to detect operating system from user agent
 */
export const detectOS = (): OperatingSystem => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.indexOf('win') !== -1) return 'Windows';
  if (userAgent.indexOf('mac') !== -1) return 'MacOS';
  if (userAgent.indexOf('linux') !== -1) return 'Linux';
  if (userAgent.indexOf('android') !== -1) return 'Android';
  if (userAgent.indexOf('ios') !== -1 || (userAgent.indexOf('mac') !== -1 && navigator.maxTouchPoints > 0))
    return 'iOS';
  return 'Unknown';
};

/**
 * Get appropriate WASM file path based on browser and OS
 */
export const getWasmPath = (): string => {
  // Use the latest version of the WASM file from CDN that matches our dependency
  const baseUrl: string = 'https://cdn.jsdelivr.net/npm/agora-extension-virtual-background@2.1.0/wasms';

  // For Safari and other browsers
  const isSafari: boolean = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const os: OperatingSystem = detectOS();

  console.log('Detected OS and browser', { os, isSafari });

  if (isSafari) {
    return `${baseUrl}/agora-wasm-safari.wasm`;
  }

  return `${baseUrl}/agora-wasm.wasm`;
};

const wasmPath: string = getWasmPath();

/**
 * Checks device compatibility for the virtual background feature
 * @returns {boolean}
 * @throws {Error} When virtual background is not supported

 */
const checkDeviceCompatibility = (): boolean => {
  const isSupported = extension.checkCompatibility();
  if (!isSupported) {
    throw new Error(`Virtual background is not supported`);
  }

  return isSupported;
};

/**
 * Initializes the virtual background extension
 * @returns {void}
 */
export const initializeVirtualBackground = async (): Promise<void> => {
  if (processor) return;
  try {
    checkDeviceCompatibility();
    AgoraRTC.registerExtensions([extension]);
    processor = extension.createProcessor();
    await processor.init(wasmPath);
    registerCostListener(logFrameCost);
    LOG.info('Virtual background processor initialized successfully');
  } catch (e) {
    LOG.error('Error initializing virtual background processor:', e);
    processor = null;
    pipedTrack = null;
    setProcessorEnabled(false);
    const error = e as Error;
    throw new Error(`Error initializing virtual background: ${error.message}`);
  }
};

/**
 * Creates a new transformed video device with background filter applied
 * @param videoTrack
 * @param videoFxConfig
 * @param videoInputId
 * @returns {Promise<TransformedVideoInput>}
 */
export const transformVideo = async (
  videoTrack: ICameraVideoTrack,
  videoFxConfig: VFXConfig,
  videoInputId: string
): Promise<{ videoTrack: ICameraVideoTrack; device: string }> => {
  await initializeVirtualBackground();

  if (!processor) {
    throw new Error('Virtual background processor is not initialized');
  }

  processor.setOptions(videoFxConfig);

  // Only set up the pipeline if this specific track isn't already piped.
  // Comparing the track reference (instead of a boolean flag) correctly handles:
  // - New track after camera fallback: pipedTrack is the old track, so we re-pipe
  // - Same track, different VB option: pipedTrack matches, so we skip piping
  // - Concurrent teardown race: pipedTrack is null, so we correctly re-pipe
  if (pipedTrack !== videoTrack) {
    LOG.info('Re-piping required: pipedTrack differs from videoTrack', {
      hasPipedTrack: !!pipedTrack,
      pipedTrackId: (pipedTrack as any)?._ID,
      videoTrackId: (videoTrack as any)?._ID
    });

    // Clean up old pipeline
    if (pipedTrack) {
      pipedTrack.unpipe();
    }
    processor.unpipe();
    videoTrack.unpipe();

    // This will throw if processorDestination has a stale upstream reference
    // from a previously released processor. The caller (changeUserBackground)
    // handles this by recreating the track to get a fresh processorDestination.
    videoTrack.pipe(processor).pipe(videoTrack.processorDestination);
    pipedTrack = videoTrack;
    LOG.info('Video track piped through virtual background processor');
  }

  if (!isProcessorEnabled) {
    await processor.enable();
    setProcessorEnabled(true);
    LOG.info('Virtual background processor enabled');
  } else {
    LOG.info('Updated virtual background options without re-enabling');
  }

  return {
    videoTrack,
    device: videoInputId
  };
};

/**
 * @deprecated
 * Temporarily disables virtual background but keeps processor for reuse
 * @returns {void}
 */
export const clearVirtualBackground = async (): Promise<void> => {
  if (!processor) {
    LOG.error('No processor found');
    return;
  }

  await processor.disable();
  processor = null;
  pipedTrack = null;
  LOG.info('Clearing virtual background');
};

/**
 * Returns the configuration for the virtual background effect
 * @param filterType
 * @param backgroundImageURL
 * @returns {{type: string, blurDegree?: number, source?: HTMLImageElement}}
 */
export const getVFXConfig = async (
  filterType: BACKGROUND_OPTIONS,
  backgroundImageURL: string = ''
): Promise<VFXConfig | undefined> => {
  if (filterType === BACKGROUND_OPTIONS.BLUR) {
    return {
      type: 'blur',
      blurDegree: 3
    };
  }

  if (filterType === BACKGROUND_OPTIONS.CUSTOM) {
    if (!backgroundImageURL) {
      return {
        type: 'color',
        color: '#5C617C'
      };
    }

    // Wait for the image to load before returning
    const img = new Image();
    // Enable CORS for canvas usage in virtual background processor
    img.crossOrigin = 'anonymous';

    return new Promise(resolve => {
      img.onload = () => {
        LOG.info('Custom background image loaded successfully', {
          width: img.width,
          height: img.height,
          src: backgroundImageURL
        });
        resolve({
          type: 'img',
          source: img
        });
      };

      img.onerror = (event: Event | string) => {
        LOG.error('Failed to load custom background image', { error: event, src: backgroundImageURL });
        // Fallback to color background if image fails to load
        resolve({
          type: 'color',
          color: '#5C617C'
        });
      };

      img.src = backgroundImageURL;
    });
  }
  return undefined;
};

/**
 * @deprecated
 * Get video input based on option
 * @param option
 * @param transformedVideoInput
 * @param selectedVideoInputId
 * @param onTransformedVideoInputChange
 * @param backgroundUrl
 * @returns {Promise<ILocalVideoTrack>}
 */
export const getVideoInput = async (
  option: BACKGROUND_OPTIONS,
  transformedVideoInput: TransformedVideoInput | null,
  selectedVideoInputId: string,
  // eslint-disable-next-line no-unused-vars
  onTransformedVideoInputChange: (input: { transformedVideoTrack?: ICameraVideoTrack; device?: string }) => void,
  backgroundUrl = ''
): Promise<{ transformedVideoTrack?: ICameraVideoTrack; device?: string } | TransformedVideoInput> => {
  const videoTrack = await AgoraRTC.createCameraVideoTrack({
    cameraId: selectedVideoInputId,
    encoderConfig: AGORA_ENCODER_CONFIG
  });
  const videoFxConfig = await getVFXConfig(option, backgroundUrl);

  const userHasBackground = option !== BACKGROUND_OPTIONS.NONE;

  let videoInput: { transformedVideoTrack?: ICameraVideoTrack; device?: string } = {
    device: selectedVideoInputId
  };

  const isFirstTimeBackgroundChange = userHasBackground && transformedVideoInput === null;

  const isUpdatingBackgroundFilter =
    userHasBackground && transformedVideoInput && transformedVideoInput?.device === selectedVideoInputId;

  const isChangingDevicesWithBackgroundFilter =
    userHasBackground && transformedVideoInput && transformedVideoInput?.device !== selectedVideoInputId;

  if (!processor) {
    throw new Error('Trying to apply virtual background without initializing the processor or invalid config');
  }

  if (isFirstTimeBackgroundChange && videoFxConfig) {
    videoInput = await transformVideo(videoTrack, videoFxConfig, selectedVideoInputId);
    onTransformedVideoInputChange(videoInput);
  } else if ((isChangingDevicesWithBackgroundFilter || isUpdatingBackgroundFilter) && videoFxConfig) {
    transformedVideoInput.transformedVideoTrack.close();
    processor.reset();
    videoInput = await transformVideo(videoTrack, videoFxConfig, selectedVideoInputId);
  }
  return videoInput || transformedVideoInput;
};
